import React, { useRef, useState } from "react";
import VirtualTable from "../../../components/custom/VirtualTable";
import { IssuedRow, UnitDetail } from "./types";
import { EmptyState } from "./shared";
import useSWR from "swr";
import { customFetcher, extractReactTableData, fileDownload } from "../../../utils";
import useUnit from "../../../customHooks/useUnit";
import apiClient from "../../../apiClient";
import SerialsModal, { useSerialsModalDataStore } from "./SerialsModal";
import moment from "moment";
import styled from "styled-components";
import ActionIcon from "../../../components/custom/ActionIcon";
import { SerialsArray } from "./useSerials";

export interface Log {
    id: number;
    actorEmail: string;
    actorName: string;
    actionLabel: string;
    itemName: string;
    createdAt: Date | string;
    departmentId: string;
    fromRef: string;
    itemId: number;
    itemTypeId: number;
    quantity: number;
    summary: string;
    toRef: string;
    unit: string;
}

interface Params {
    loadUnit: (serial: string) => Promise<void>;
}

export default function LogView({
    loadUnit
}: Params) {
    const unit = useUnit();
    const {
        data
    } = useSWR(`/inventory/activity?unit=${unit}`, customFetcher<Log>, {
        fallbackData: []
    });

    const viewSerials = useSerialsModalDataStore(s => s.viewSerials);

    const tableRef = useRef();

    const handleRowClick = async (row) => {
        if (!row.action?.startsWith('inventory.')) return; // config rows have no units
        viewSerials(row.id, `for ${row.summary} on ${moment(row.createdAt).format('YYYY-MM-DD HH:mm')}`, 'activity');
    };

    const ACTION_LABELS: Record<string, string> = {
        'inventory.add': 'Added',
        'inventory.assign': 'Assigned',
        'inventory.transfer': 'Transferred',
        'inventory.return': 'Returned',
        'inventory.status': 'Status change',
        'department.create': 'Dept created',
        'department.update': 'Dept updated',
        'department.delete': 'Dept deleted',
        'department.access.update': 'Access updated',
        'itemType.create': 'Type created',
        'itemType.update': 'Type updated',
        'itemType.delete': 'Type deleted',
        'attributeDefinition.create': 'Attribute added',
        'attributeDefinition.update': 'Attribute updated',
        'attributeDefinition.delete': 'Attribute deleted',
    };

    // @ts-ignore
    const rows = data.map((r) => ({ ...r, actionLabel: ACTION_LABELS[r.action] ?? r.action })).sort((a, b) => +moment(b.createdAt) - +moment(a.createdAt));

    const exportData = () => {
        const data = extractReactTableData(tableRef, [], [{
            key: 'createdAt',
            name: 'Date',
            datetime: true
        }, {
            key: 'actorEmail',
            name: 'User'
        }, {
            key: 'itemName',
            name: 'Item'
        }, {
            key: 'actionLabel',
            name: 'Action'
        }, {
            key: 'summary',
            name: 'Details'
        }, {
            key: 'quantity',
            name: 'Qty'
        }]);
        fileDownload(data, `inventory_log_${unit}_${moment().format('YYYY-MM-DD')}.csv`);
    }

    return (
        <div>
            <EndContainer>
                <ActionIcon
                    type={'csv'}
                    onClick={exportData}
                />
            </EndContainer>
            <VirtualTable<Log>
                data={rows}
                mapping={[
                    { name: 'Date', value: 'createdAt', datetime: true }, // stored as UTC
                    { name: 'User', value: 'actorEmail', email: true },        // or 'actorName' if you skipped the tweak
                    { name: 'Item', value: 'itemName', customWidth: 200 },        // or 'actorName' if you skipped the tweak
                    { name: 'Action', value: 'actionLabel', customWidth: 100 },  // see label map below; or use 'action' raw
                    { name: 'Details', value: 'summary' },
                    { name: 'Qty', value: 'quantity', number: true, customWidth: 75 },
                ]}
                handleClick={item => handleRowClick(item)}
                reactTableRef={tableRef}
            />
        </div>
    );
}

const EndContainer = styled.div`
    display: flex;
    justify-content: flex-end;
`;